import React, { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, FileUp, CheckCircle2, AlertCircle, TrendingUp, TrendingDown } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

export default function Fatture() {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState(null);

  const handleSelect = () => fileInputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setReport(null);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/invoices/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReport(data);
      if (data.total_price_updates > 0) {
        toast.success(`${data.total_price_updates} prezzi aggiornati`);
      } else if (data.total_matched > 0) {
        toast.info("Fattura importata: nessuna variazione di prezzo");
      } else {
        toast.warning("Nessun prodotto trovato in archivio");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore importazione");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 4 });

  return (
    <div className="space-y-6" data-testid="fatture-page">
      <div>
        <p className="label-tag">ARCHIVIO</p>
        <h1 className="font-brand text-4xl sm:text-5xl font-light mt-1">Fatture</h1>
        <p className="mt-2" style={{ color: "var(--ds-text-secondary)" }}>
          Importa fatture elettroniche (XML / P7M / ZIP) per aggiornare automaticamente i prezzi d'acquisto.
        </p>
      </div>

      <Card className="ds-card">
        <CardHeader>
          <CardTitle className="font-brand text-2xl font-light flex items-center gap-2">
            <FileUp size={20} /> Importa fattura
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className="border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-white/50 transition"
            style={{ borderColor: "var(--ds-border)" }}
            onClick={handleSelect}
            data-testid="invoice-dropzone"
          >
            <Upload size={36} style={{ color: "var(--ds-primary)" }} />
            <p className="mt-3 font-medium">Clicca per caricare</p>
            <p className="text-sm mt-1" style={{ color: "var(--ds-text-secondary)" }}>
              Formati supportati: <b>.xml</b>, <b>.p7m</b> o <b>.zip</b> (più fatture)
            </p>
            <Button
              type="button"
              disabled={uploading}
              className="ds-btn-primary mt-4"
              style={{ background: "var(--ds-primary)" }}
              data-testid="invoice-upload-btn"
            >
              {uploading ? "Importazione in corso..." : "Seleziona file"}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xml,.p7m,.zip"
              onChange={handleFile}
              className="hidden"
              data-testid="invoice-file-input"
            />
          </div>

          <div className="mt-4 text-xs" style={{ color: "var(--ds-text-secondary)" }}>
            <p>
              <b>Come funziona:</b> l'app legge gli articoli della fattura e cerca corrispondenza con i prodotti in
              archivio nell'ordine: <i>codice articolo fornitore</i> → <i>codice a barre</i> → <i>nome esatto</i>.
              Se trova un prezzo diverso, aggiorna il prodotto e registra una variazione visibile in Dashboard e nel
              Dettaglio prodotto.
            </p>
          </div>
        </CardContent>
      </Card>

      {report && (
        <Card className="ds-card" data-testid="invoice-report">
          <CardHeader>
            <CardTitle className="font-brand text-2xl font-light">Report importazione</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric label="File processati" value={report.processed_files} />
              <Metric label="Righe totali" value={report.total_lines} />
              <Metric label="Prodotti trovati" value={report.total_matched} />
              <Metric label="Prezzi aggiornati" value={report.total_price_updates} highlight />
            </div>

            {report.errors?.length > 0 && (
              <div className="rounded-lg p-3 text-sm" style={{ background: "#FEF2F2", color: "#991B1B" }}>
                <div className="flex items-center gap-2 font-medium mb-1">
                  <AlertCircle size={16} /> Errori
                </div>
                <ul className="list-disc pl-5">
                  {report.errors.map((e, i) => (
                    <li key={`err-${i}`}><b>{e.file}:</b> {e.error}</li>
                  ))}
                </ul>
              </div>
            )}

            {report.results.map((r) => (
              <div key={`res-${r.filename}`} className="border rounded-lg p-4" style={{ borderColor: "var(--ds-border)" }}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      <CheckCircle2 size={16} style={{ color: r.supplier_matched ? "var(--ds-primary)" : "#A0A0A0" }} />
                      {r.filename}
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--ds-text-secondary)" }}>
                      Fattura n° {r.numero || "—"} • {r.data || "—"} • Cedente: {r.cedente || "—"}
                      {!r.supplier_matched && <span className="ml-2" style={{ color: "var(--ds-danger)" }}>(fornitore non in archivio)</span>}
                    </div>
                  </div>
                  <div className="text-xs">
                    {r.lines_matched}/{r.lines_total} prodotti trovati • {r.price_updates.length} aggiornati
                  </div>
                </div>

                {r.price_updates.length > 0 && (
                  <div className="mt-3">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Prodotto</TableHead>
                          <TableHead className="text-right">Vecchio prezzo</TableHead>
                          <TableHead className="text-right">Nuovo prezzo</TableHead>
                          <TableHead className="text-right">Variazione</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {r.price_updates.map((u) => (
                          <TableRow key={`u-${u.prodotto_id}`}>
                            <TableCell className="font-medium text-sm">{u.nome}</TableCell>
                            <TableCell className="text-right text-sm">€ {fmt(u.vecchio_prezzo)}</TableCell>
                            <TableCell className="text-right text-sm font-medium">€ {fmt(u.nuovo_prezzo)}</TableCell>
                            <TableCell className="text-right text-sm">
                              <span
                                className="inline-flex items-center gap-1"
                                style={{ color: u.variazione_perc >= 0 ? "var(--ds-danger)" : "#15803D" }}
                              >
                                {u.variazione_perc >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                {u.variazione_perc >= 0 ? "+" : ""}{u.variazione_perc.toFixed(2)}%
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {r.lines_unmatched.length > 0 && (
                  <details className="mt-3 text-xs">
                    <summary className="cursor-pointer" style={{ color: "var(--ds-text-secondary)" }}>
                      {r.lines_unmatched.length} prodotti non trovati in archivio
                    </summary>
                    <ul className="list-disc pl-5 mt-2" style={{ color: "var(--ds-text-secondary)" }}>
                      {r.lines_unmatched.map((u, i) => (
                        <li key={`u-${r.filename}-${i}`}>
                          {u.descrizione || "—"} {u.codice_articolo ? `(${u.codice_articolo})` : ""} — € {fmt(u.prezzo_unitario)}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Metric({ label, value, highlight }) {
  return (
    <div className="rounded-lg p-3" style={{ background: highlight ? "var(--ds-primary)" : "var(--ds-surface)", color: highlight ? "#fff" : "var(--ds-text-primary)" }}>
      <div className="label-tag" style={{ color: highlight ? "#FCE8DE" : undefined }}>{label}</div>
      <div className="text-2xl font-light mt-1">{value}</div>
    </div>
  );
}
